import React, { useImperativeHandle, useRef, useState } from 'react';
import { Alert, Button, Modal, Result, Space, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { useHistory } from 'react-router-dom';
import usePollAnimationFrame from '@/hooks/usePollAnimationFrame';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';
import BillSettle from '@/pages/bill/settle';
import { SettleDrawerRef } from '@/pages/bill/interface';
import { fetchCardInfo } from '@/api/card';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import { LockVendorMap } from '../../config';
import {
  CardOperateProcessingStatus,
  CardStatus,
  emptyAndDataAccuracyStrategyMap,
  ICardInfo,
  MyDoorLockCardClass,
  SingleCardProxyClass,
} from '../helper';
import styles from '../index.module.scss';

const ReadDoorCardModal = ({ innerRef, enabledVendor, readCallBack }) => {
  const [open, setOpen] = useState(false);

  const history = useHistory();

  const [cardInfo, setCardInfo] = useState<Partial<ICardInfo>>({});
  const [requestStatus, setRequestStatus] = useState<CardOperateProcessingStatus>(
    CardOperateProcessingStatus.Init,
  );

  const usePoll = usePollAnimationFrame();

  // 连接实例
  const instanceRef = useRef<MyDoorLockCardClass>();
  const cardInstance: MyDoorLockCardClass = new SingleCardProxyClass();
  instanceRef.current = cardInstance;

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  const getCardInfo = async ({ localCardInfo }) => {
    const { card_id: cardNo } = localCardInfo;

    try {
      const res: any = await fetchCardInfo({ cardNo });
      if (res.code === 0) {
        const info = res.data;
        return info;
      }
      return {};
    } catch (error) {
      throw new Error(error.msg || '获取卡信息失败');
    }
  };

  const pollReadCard = () => {
    const strategyObj = emptyAndDataAccuracyStrategyMap[enabledVendor?.lockCode];

    setRequestStatus(CardOperateProcessingStatus.Loading);
    usePoll
      .startPoll(
        () => cardInstance.readCard({ lockType: LockVendorMap[enabledVendor?.lockCode] }),
        strategyObj.successFulCodeList,
      )
      .then(
        async (res) => {
          console.log('readData', res);
          const isEmpty = strategyObj.isEmptyCard(res);
          const localCardInfo: ICardInfo = strategyObj.updateCardStatus(res);
          // 请求卡具体信息
          if (!isEmpty) {
            try {
              const requestCardInfo = await getCardInfo({ localCardInfo });
              setCardInfo({ ...localCardInfo, requestCardInfo });
              setRequestStatus(CardOperateProcessingStatus.RequestSuccess);

              endUploadDoorInfo({ result: UploadStatus.Success, roomNo: requestCardInfo?.roomNo });
            } catch (error) {
              setRequestStatus(CardOperateProcessingStatus.RequestError);

              endUploadDoorInfo({ result: UploadStatus.Fail, remark: error.msg });
            }
          } else {
            // 空白卡
            setCardInfo({ cardStatus: CardStatus.Empty });
            setRequestStatus(CardOperateProcessingStatus.RequestSuccess);

            // 读卡时空白卡上报
            endUploadDoorInfo({
              result: UploadStatus.Success,
              roomNo: intl.get('device_door_empty_card'),
            });
          }
        },
        (error) => {
          console.log('receive=>>>>error', error.message);
          setCardInfo({ cardStatus: CardStatus.Exception });
          setRequestStatus(CardOperateProcessingStatus.RequestError);

          endUploadDoorInfo({ result: UploadStatus.Fail, remark: error.msg });
        },
      )
      .catch((error) => {
        setCardInfo({ cardStatus: CardStatus.Exception });
        setRequestStatus(CardOperateProcessingStatus.RequestError);

        endUploadDoorInfo({ result: UploadStatus.Fail, remark: error.msg });
      });
  };

  const closeModal = () => {
    setOpen(false);

    usePoll.endPoll();
  };

  const resetState = () => {
    setCardInfo({});
    setRequestStatus(CardOperateProcessingStatus.Init);
  };

  const retry = async () => {
    await startUploadDoorInfo({ type: CardUsage.Read });

    resetState();

    pollReadCard();
  };

  useImperativeHandle(
    innerRef,
    () => ({
      openModal() {
        setOpen(true);

        retry();
      },
      closeModal,
    }),
    [],
  );

  const settleRef = useRef<SettleDrawerRef>(null);

  // 结账
  const gotoSettle = () => {
    if (readCallBack && typeof readCallBack === 'function') readCallBack();
    settleRef.current!.openDrawer({
      resvNo: cardInfo.requestCardInfo.resvNo,
    });
  };

  // 订单详情
  const gotoOrderDetail = () => {
    if (readCallBack && typeof readCallBack === 'function') readCallBack();
    setTimeout(() => {
      history.push(`/order/room-order/${cardInfo.requestCardInfo.resvNo}`);
    }, 1000);
  };

  const modalFooter = () => {
    if (
      [CardStatus.Empty, CardStatus.Exception].includes(cardInfo.cardStatus) ||
      requestStatus === CardOperateProcessingStatus.RequestError
    ) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => retry()}>
            {intl.get('app_confirm_retry_btn')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    if (requestStatus === CardOperateProcessingStatus.RequestSuccess) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => gotoSettle()}>
            {intl.get('app_checkout')}
          </Button>
          <Button type="primary" onClick={() => gotoOrderDetail()}>
            {intl.get('app_order')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    return <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>;
  };

  const renderHint = () => {
    if (requestStatus === CardOperateProcessingStatus.Loading) {
      return <Alert message={intl.get('cpm_cards_put_on_write_mechine')} type="info" showIcon />;
    }

    if (
      [CardStatus.Exception].includes(cardInfo?.cardStatus) ||
      requestStatus === CardOperateProcessingStatus.RequestError
    ) {
      return (
        <Alert
          message={intl.get('cpm_cards_room_card_status_abnormal_canot_read')}
          type="error"
          showIcon
        />
      );
    }

    if (
      [CardStatus.Empty].includes(cardInfo?.cardStatus) ||
      requestStatus === CardOperateProcessingStatus.RequestSuccess
    ) {
      return <Alert message={intl.get('cpm_cards_read_room_card_info')} type="success" showIcon />;
    }

    return null;
  };

  const renderCardInfo = () => {
    if (requestStatus === CardOperateProcessingStatus.Loading) {
      return (
        <div className={styles.readCardLoading}>
          <Spin
            tip={intl.get('cpm_cards_info_reding')}
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      );
    }

    if (
      [CardStatus.Exception].includes(cardInfo?.cardStatus) ||
      requestStatus === CardOperateProcessingStatus.RequestError
    ) {
      return (
        <Result
          style={{ height: 200 }}
          status="error"
          subTitle={intl.get('cpm_cards_info_abnormal')}
        />
      );
    }

    if ([CardStatus.Empty].includes(cardInfo?.cardStatus)) {
      return (
        <Result
          style={{ height: 200 }}
          status="success"
          subTitle={intl.get('cpm_cards_current_empty_card')}
        />
      );
    }

    if (requestStatus === CardOperateProcessingStatus.RequestSuccess) {
      return (
        <ul className={styles.cardInfo}>
          <li>
            <label>{intl.get('app_room')}</label>
            <span>{cardInfo?.requestCardInfo?.roomNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('device_lock_number')}</label>
            <span>{cardInfo?.requestCardInfo?.lockNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('manage_license_field_start_label')}</label>
            <span>
              {getTimeByUtcOffset(cardInfo?.requestCardInfo?.validityStartTime, DATE_TIME_FORMAT) ||
                '-'}
            </span>
          </li>
          <li>
            <label>{intl.get('cpm_cards_end_validity_period')}</label>
            <span>
              {getTimeByUtcOffset(cardInfo?.requestCardInfo?.validityEndTime, DATE_TIME_FORMAT) ||
                '-'}
            </span>
          </li>
        </ul>
      );
    }

    return null;
  };

  return (
    <>
      <Modal
        width={560}
        title={intl.get('app_read_room_card')}
        open={open}
        maskClosable={false}
        keyboard={false}
        footer={modalFooter()}
        onCancel={() => closeModal()}
      >
        {renderHint()}
        {renderCardInfo()}

        <BillSettle ref={settleRef} settleCallback={() => {}} />
      </Modal>
    </>
  );
};

export default ReadDoorCardModal;
